// The shape of an availability pattern, and the pure functions that make one.
//
// These lived in two component files, which is why a bug hid in them for a
// week: patternFromGroup never copied changeoverMinutes, so every Edit and
// every Copy handed the builder a pattern with the field missing, the select
// fell back to `?? 0` and displayed "None", and saving wrote that 0 over
// whatever the barn had set. A pattern entered as 45+15 came back as a flat
// 45-minute grid the next time anybody touched it, silently.
//
// Nothing could catch that, because a pure data function sitting inside a JSX
// component is a pure data function nothing can import. They're here now, and
// web/test/pattern.test.mjs holds them to it.

/** ISO date n days from iso. Pure string math, no timezone. */
export function addDaysISO(iso, n) {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/**
 * A blank pattern.
 *
 * The single source of truth for what fields a pattern HAS. Everything that
 * builds one spreads this first, so a field added here arrives everywhere with
 * a value rather than as undefined.
 */
export function emptyPattern(today) {
  return {
    weekdays: [],
    startTime: '15:00',
    endTime: '18:00',
    slotMinutes: 45,
    // 15 minutes for new patterns: untacking one horse and tacking the next
    // takes about that. Existing blocks stay at 0 — the column defaults there,
    // so nothing already on a calendar shifts under anyone.
    changeoverMinutes: 15,
    capacity: 1,
    lessonTypeId: '',
    effectiveFrom: today,
    effectiveUntil: addDaysISO(today, 84),
  };
}

/**
 * Slots one day of this pattern yields. Mirrors computeSlots exactly.
 *
 * Lessons repeat every (lesson + changeover), but the last one only needs room
 * for the lesson itself — nobody waits through a changeover at the end of the
 * day. Diverging from the server here would put a count under the pills that
 * the calendar below then contradicts.
 */
export function slotsPerDay(p) {
  const mins = (t) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3));
  const span = mins(p.endTime) - mins(p.startTime);
  const step = p.slotMinutes + (p.changeoverMinutes ?? 0);
  if (p.slotMinutes <= 0 || step <= 0 || span < p.slotMinutes) return 0;
  return Math.floor((span - p.slotMinutes) / step) + 1;
}

/**
 * One pattern becomes one per hour.
 *
 * "Chunk days into one hour blocks for everyone." A trainer opening Wednesday
 * 9 to 12 means three things, not one — and three things can be changed one at
 * a time, which is the whole point. Ten o'clock becomes jumping without
 * touching nine or eleven.
 *
 * It also makes the hours on a calendar line up with the hours a barn talks
 * in, which is what turns a weekly view into a grid rather than a drawing of
 * one. The lesson types already fit: a 45-minute private plus a 15-minute
 * changeover IS an hour, and so is a flat 60-minute group.
 *
 * THE BLOCK IS NOT ALWAYS SIXTY MINUTES
 *
 * It is one hour, or one lesson's worth if a lesson is longer than that. A
 * 90-minute clinic in a 60-minute block would save and then quietly yield
 * nothing, because no whole lesson fits inside it. Shorter lessons do NOT
 * shrink the block: a 30-minute training ride gives an hour holding two of
 * them, which is the rule as asked for.
 *
 * Chunking runs from the start time rather than from the clock. Somebody who
 * teaches 9:30 to 12:30 means that, and snapping them to 9:00 or 10:00 would
 * move hours they chose.
 */
export function splitIntoHours(p) {
  const mins = (t) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3));
  const hhmm = (m) =>
    `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

  const from = mins(p.startTime);
  const to = mins(p.endTime);
  const lesson = p.slotMinutes;
  const step = lesson + (p.changeoverMinutes ?? 0);
  if (!(lesson > 0) || !(step > 0) || to <= from) return [];

  const size = Math.max(60, step);
  const out = [];
  for (let start = from; start < to; start += size) {
    const end = Math.min(start + size, to);
    // A tail too short to hold one lesson is dropped rather than saved as a
    // block that yields nothing. The builder says so before it happens.
    if (end - start < lesson) break;
    out.push({ ...p, startTime: hhmm(start), endTime: hhmm(end) });
  }
  return out;
}

/**
 * What splitting would leave behind, in minutes. Zero when the range divides
 * cleanly, which is the normal case.
 */
export function remainderMinutes(p) {
  const mins = (t) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3));
  const blocks = splitIntoHours(p);
  if (blocks.length === 0) return Math.max(0, mins(p.endTime) - mins(p.startTime));
  return mins(p.endTime) - mins(blocks[blocks.length - 1].endTime);
}

/**
 * A saved group of blocks, back into the shape the builder edits.
 *
 * Spreads emptyPattern first on purpose — see the note at the top of this file.
 */
export function patternFromGroup(g) {
  const b = g.blocks[0];
  return {
    ...emptyPattern(b.effectiveFrom),
    weekdays: g.blocks.map((x) => x.weekday).sort((a, c) => a - c),
    startTime: b.startTime,
    endTime: b.endTime,
    slotMinutes: b.slotMinutes,
    changeoverMinutes: b.changeoverMinutes ?? 0,
    capacity: b.capacity,
    lessonTypeId: b.lessonTypeId || '',
    effectiveFrom: b.effectiveFrom,
    effectiveUntil: b.effectiveUntil,
  };
}

/**
 * The same pattern again, over the stretch of time after it.
 *
 * Barns run in terms and the next one usually looks like the last, so
 * duplicating keeps the days and hours and moves only the dates — starting the
 * day after the old one ended, for the same length it ran.
 */
export function nextTermFrom(group, today) {
  const p = patternFromGroup(group);
  if (!p.effectiveUntil) return { ...p, effectiveFrom: today, effectiveUntil: null };
  const start = addDaysISO(p.effectiveUntil, 1);
  const lengthDays = Math.max(
    1,
    Math.round(
      (Date.parse(p.effectiveUntil + 'T00:00:00Z') - Date.parse(p.effectiveFrom + 'T00:00:00Z')) /
        86400000,
    ),
  );
  const from = start < today ? today : start;
  return { ...p, effectiveFrom: from, effectiveUntil: addDaysISO(from, lengthDays) };
}
